import Chroma from 'chroma-js';
import luminanceScales from './luminanceScales';
const shmonsole = console; // because linters don't understand humor

/** 
 * createColorSwatch represents a single color of a colorStack. Rarely used by itself, it is usually instantiated by colorStack.
 
  * @param {string} seedColor CSS formated color value: hex ('#FFFFFF'), RGB ('rgb(255,255,255)'), HSL ('hsl(360,0%,100%)'), web color ('white'), etc. This color will have its luminance adjusted based on the nominalContrast provided.

  * @param {enum} themeName "default" or "dark", determines which luminance scale is used.

  * @param {enum} nominalContrast "zero", "10", "15", "30", "45", "70", "100", "150", or "max"

  * @param {string} adjustedColor CSS formated color value. The adjustedColor can override the seed color (when complexColorSwatch is finalized). If adjustedColor is provided, the "useAdjusted" property in the return value will be set to "true".

  * @returns {object} complexColorSwatch
  {
    nominalContrast: string, // read-only
    generatedColor: string, // read-only
    adjustedColor: string,
    useAdjusted: boolean,
    exactColor: string, // this property can be added manually, or may be added by colorStack
    useExact: boolean // this property can be added manually, or may be added by colorStack
  }
 */
export function createColorSwatch(
  seedColor,
  themeName,
  nominalContrast,
  adjustedColor
) {
  if (Chroma.valid(seedColor)) {
    const validAdjustedColor = Chroma.valid(adjustedColor);
    const generatedColor = new Chroma(seedColor)
      .luminance(getLuminance(themeName, nominalContrast))
      .css();

    const swatch = {
      get nominalContrast() {
        return nominalContrast;
      },
      get generatedColor() {
        return generatedColor;
      },
      adjustedColor: validAdjustedColor ? adjustedColor : undefined,
      useAdjusted: validAdjustedColor
    };

    return swatch;
  }

  /* error if seedColor is not valid */
  shmonsole.error(
    `[createColorSwatch]: "${seedColor}" is not a valid seedColor. \nProvide in CSS color value format`
  );
  return undefined;
}
export default createColorSwatch;

/**
 * @function finalizeColorSwatch takes a complexColorSwatch instance, and returns a { [nominalContrast]: finalizedValue } key-value pair.
 */
export function finalizeColorSwatch({
  generatedColor,
  adjustedColor,
  exactColor,
  useExact,
  useAdjusted,
  nominalContrast
}) {
  return {
    [nominalContrast]:
      (useExact && exactColor) ||
      (useAdjusted && adjustedColor) ||
      generatedColor
  };
}

/**
 * @function finalizeColorSwatchValue takes a complexColorSwatch instance and returns only the value. Example: finalizeColorSwatchValue(createColorSwatch('rgb(0,106,225)', 'default', '45')) // returns 'rgb(9,111,226)'
 */
export function finalizeColorSwatchValue(swatch) {
  return Object.values(finalizeColorSwatch(swatch))[0];
}

function getLuminanceScale(themeName) {
  switch (themeName) {
    case 'default':
      return luminanceScales.default;
    case 'dark':
      return luminanceScales.dark;
    default:
      shmonsole.error(
        `[createColorSwatch] themeName "${themeName}" is not valid, "default" was used.`
      );
      return luminanceScales.default;
  }
}

function getLuminance(themeName, _nominalContrast) {
  const nominalContrast = `${_nominalContrast}`;
  const luminanceScale = getLuminanceScale(themeName);
  const hasNominalContrast = Object.keys(luminanceScale).includes(
    nominalContrast
  );
  return hasNominalContrast
    ? luminanceScale[nominalContrast]
    : luminanceScale.max;
}
